// File: src/components/auth/LoginForm.tsx
import React, { useEffect } from 'react'; // ADDED useEffect import
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate, Link } from 'react-router-dom';
import Input from '../common/Input';
import Button from '../common/Button';
import { toast } from 'react-hot-toast';
import authService, { LoginCredentials } from '../../services/authService';
import { AxiosError } from 'axios';
import { ApiErrorDetail } from '../../types';

type LoginFormInputs = {
  email: string;
  password: string;
};

const LoginForm: React.FC = () => {
  const { t } = useTranslation(['auth', 'validation', 'common']);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = React.useState(false);
  const [showResendOption, setShowResendOption] = React.useState(false);
  const [resendEmail, setResendEmail] = React.useState('');
  const [isResending, setIsResending] = React.useState(false);

  // Create schema with translated messages
  const loginSchema = z.object({
    email: z.string().email({ message: t('validation:email_invalid') }),
    password: z.string().min(6, { message: t('validation:password_min', { min: 6 }) }),
  });

  // --- NEW: Mount/Unmount Logs ---
  useEffect(() => {
    console.log("LoginForm: Mounted!");
    return () => {
      console.log("LoginForm: Unmounting!");
    };
  }, []);

  useEffect(() => {
    console.log("LoginForm: showResendOption state updated to:", showResendOption);
  }, [showResendOption]);
  // --- END NEW Logs ---


  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormInputs>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormInputs) => {
    setIsLoading(true);
    // console.log("LoginForm: Resetting showResendOption to false at start of onSubmit."); // Optional: verify reset
    setShowResendOption(false); // Reset on new login attempt
    setResendEmail('');

    try {
      const credentials: LoginCredentials = {
        username: data.email, // API uses 'username' for email
        password: data.password,
      };
      await login(credentials);
      toast.success(t('auth:login.success_message'));
      navigate('/app'); // Redirect to /app, AppIndexRedirect will handle specific role
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorDetail>;
      
      // --- START DEBUG LOGS (already there from last step) ---
      console.log("--- DEBUG: Login Error Handling ---");
      console.log("Full Axios Error Object:", axiosError);
      console.log("Error Response Status:", axiosError.response?.status);
      console.log("Error Response Detail:", axiosError.response?.data?.detail);
      console.log("Type of errorMessage:", typeof axiosError.response?.data?.detail);

      const expectedMessage = "Your account is pending verification. Please check your email.";
      console.log("Expected message for comparison:", expectedMessage);
      console.log("Does status match 403?", axiosError.response?.status === 403);
      console.log("Does detail message match exactly?", axiosError.response?.data?.detail === expectedMessage);
      // --- END DEBUG LOGS ---

      const errorMessage = axiosError.response?.data?.detail;

      if (typeof errorMessage === 'string') {
        if (axiosError.response?.status === 403 && errorMessage === expectedMessage) {
          toast.error(errorMessage);
          setShowResendOption(true);
          setResendEmail(data.email);
          console.log("DEBUG: Condition met! showResendOption set to TRUE inside catch.");
        } else {
          toast.error(errorMessage);
          console.log("DEBUG: Condition NOT met. showResendOption remains FALSE inside catch.");
        }
      } else {
        toast.error(t('auth:login.error_unexpected'));
        console.log("DEBUG: Error message is not a string. showResendOption remains FALSE inside catch.");
      }
      // console.log("DEBUG: Current value of showResendOption after catch block:", showResendOption); // This might log stale value
      console.log("--- END DEBUG: Login Error Handling ---");

    } finally {
      setIsLoading(false);
    }
  };

  // Handle resend verification email
  const handleResendVerification = async () => {
    if (!resendEmail) return;
    setIsResending(true);
    try {
      const response = await authService.resendVerificationEmail({ email: resendEmail });
      toast.success(response.msg);
      setShowResendOption(false); // Hide button after successful resend
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorDetail>;
      const errorMessage = axiosError.response?.data?.detail;
      if (typeof errorMessage === 'string') {
        // Specific error messages from backend report
        if (errorMessage.includes("You can request a new verification email every")) {
          toast.error(t('auth:verification.rate_limit_error'));
        } else if (errorMessage === "User with this email not found.") {
          toast.error(t('auth:verification.user_not_found'));
        } else if (errorMessage === "This account is not pending verification.") {
          toast.error(t('auth:verification.not_pending'));
          setShowResendOption(false); // Also hide if account is already active
        } else {
          toast.error(errorMessage);
        }
      } else {
        toast.error(t('auth:verification.resend_error'));
      }
    } finally {
      setIsResending(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <Input
        label={t('auth:login.email_label')}
        type="email"
        id="email"
        autoComplete="username"
        error={errors.email?.message}
        {...register('email')}
      />
      <Input
        label={t('auth:login.password_label')}
        type="password"
        id="password"
        autoComplete="current-password"
        error={errors.password?.message}
        {...register('password')}
      />
      <div className="flex items-center justify-between">
        <div className="text-sm">
          <Link to="/forgot-password" className="font-medium text-primary hover:text-primary-dark">
            {t('auth:login.forgot_password_link')}
          </Link>
        </div>
      </div>
      <Button type="submit" isLoading={isLoading} fullWidth>
        {t('auth:login.sign_in_button')}
      </Button>

      {/* Resend verification option */}
      {showResendOption && (
        <div className="mt-4 text-center">
          <p className="text-sm text-neutral-dark mb-2">{t('auth:verification.did_not_receive')}</p>
          <Button 
            type="button" 
            variant="outline" 
            onClick={handleResendVerification} 
            isLoading={isResending} 
            fullWidth
            disabled={isResending}
          >
            {t('auth:verification.resend_button')}
          </Button>
        </div>
      )}

      <div className="mt-4 text-center text-sm text-neutral-dark space-y-2">
        <p>
          {t('auth:login.client_invitation_text')}
        </p>
        <p>
          {t('auth:login.coach_question')}{' '}
          <Link to="/register/coach" className="font-medium text-primary hover:text-primary-dark">
            {t('auth:login.coach_register_link')}
          </Link>
        </p>
        <p>
          {t('auth:login.looking_for_coach')}{' '}
          <Link to="/coaches" className="font-medium text-primary hover:text-primary-dark">
            {t('auth:login.browse_coaches_link')}
          </Link>
        </p>
      </div>
    </form>
  );
};

export default LoginForm;